import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productService } from '../lib/firebaseService';
import { Calendar, ArrowDownUp, AlertTriangle, Search, Clock, Box, ClipboardCheck, Check, Loader2 } from 'lucide-react';
import InventoryModal from '../components/InventoryModal';
import '../styles/components-extended.css';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { t } from '../utils/translation';

export default function Inventory() {
    const { currentUser } = useAuth();
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [view, setView] = useState('stock'); // 'stock', 'movements', 'audit'
    const [searchTerm, setSearchTerm] = useState('');

    // Audit State
    const [auditCounts, setAuditCounts] = useState({});
    const [isSubmittingAudit, setIsSubmittingAudit] = useState(false);

    const { data: products } = useQuery({ queryKey: ['products'], queryFn: productService.getProducts });
    const { data: movements } = useQuery({ queryKey: ['movements'], queryFn: productService.getMovements });

    const movementMutation = useMutation({
        mutationFn: productService.addMovement,
        onSuccess: () => {
            queryClient.invalidateQueries(['products']);
            queryClient.invalidateQueries(['movements']);
        }
    });

    const handleSaveMovement = async (data) => {
        await movementMutation.mutateAsync(data);
    };

    const handleAuditInput = (productId, val) => {
        setAuditCounts(prev => ({
            ...prev,
            [productId]: val
        }));
    };

    const handleFinishAudit = async () => {
        setIsSubmittingAudit(true);
        try {
            const updates = [];
            // Loop through all products that have an entry
            for (const [id, actualQty] of Object.entries(auditCounts)) {
                if (actualQty === '' || actualQty === null) continue;

                const product = products.find(p => p.id === id);
                if (!product) continue;

                updates.push(productService.reconcileProduct(
                    id,
                    actualQty,
                    product.stock,
                    currentUser?.email
                ));
            }

            // Execute all reconcile calls
            await Promise.all(updates);

            // Cleanup
            setAuditCounts({});
            setView('stock');
            queryClient.invalidateQueries(['products']);
            queryClient.invalidateQueries(['movements']);
            // Optional: Show toast success here
        } catch (error) {
            console.error("Audit failed", error);
            alert("Failed to submit audit. Please try again.");
        } finally {
            setIsSubmittingAudit(false);
        }
    };

    const lowStockCount = products?.filter(p => p.stock < (p.lowStockThreshold || 10)).length || 0;

    const filteredProducts = products?.filter(p =>
        (p.brand + ' ' + p.name).toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    return (
        <div className="max-w-[1200px] mx-auto">
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('INVENTORY.TITLE')}</h1>
                    <p className="page-subtitle">{t('INVENTORY.SUBTITLE')}</p>
                </div>
                <button onClick={() => setIsModalOpen(true)} className="btn btn-primary h-10 px-6 shadow-sm hover:shadow-md">
                    <ArrowDownUp size={18} className="mr-2" />
                    {t('INVENTORY.ADD_MOVEMENT')}
                </button>
            </div>

            {lowStockCount > 0 && view === 'stock' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center justify-between text-red-700 shadow-sm">
                    <div className="flex items-center">
                        <div className="bg-white p-2 rounded-full mr-3 shadow-sm text-red-600">
                            <AlertTriangle size={20} />
                        </div>
                        <span className="font-semibold text-lg">{lowStockCount} {t('DASHBOARD.ITEMS_NEED_RESTOCK')}</span>
                    </div>
                    <button className="text-sm font-bold bg-white px-3 py-1.5 rounded-lg border border-red-100 hover:bg-red-50 text-red-600 transition-colors">
                        {t('COMMON.ACTIONS')}
                    </button>
                </div>
            )}

            {/* Modern Tabs */}
            {/* Premium Segmented Control (Standard CSS) */}
            <div className="segmented-control-container mb-8">
                <button
                    className={`segmented-btn ${view === 'stock' ? 'active' : ''}`}
                    onClick={() => setView('stock')}
                >
                    <Box size={18} />
                    {t('INVENTORY.TAB_STOCK')}
                </button>
                <button
                    className={`segmented-btn ${view === 'movements' ? 'active' : ''}`}
                    onClick={() => setView('movements')}
                >
                    <Clock size={18} />
                    {t('INVENTORY.TAB_HISTORY')}
                </button>
                <button
                    className={`segmented-btn ${view === 'audit' ? 'active' : ''}`}
                    onClick={() => setView('audit')}
                >
                    <ClipboardCheck size={18} />
                    {t('INVENTORY.TAB_AUDIT')}
                </button>
            </div>

            {view === 'stock' ? (
                <div className="content-card">
                    <div className="mb-6">
                        <div className="search-bar w-full md:w-96">
                            <Search className="search-icon" size={18} />
                            <input
                                type="text"
                                placeholder={t('COMMON.SEARCH')}
                                className="input search-input"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>{t('PRODUCTS.TABLE.PRODUCT')}</th>
                                    <th>{t('PRODUCTS.TABLE.CATEGORY')}</th>
                                    <th className="hidden-mobile">{t('PRODUCTS.TABLE.FORMAT')}</th>
                                    <th className="hidden-mobile">{t('INVENTORY.TABLE.UNIT_COST')}</th>
                                    <th>{t('COMMON.STATUS')}</th>
                                    <th>{t('INVENTORY.TABLE.QUANTITY')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map(p => {
                                    const isLow = p.stock < (p.lowStockThreshold || 10);
                                    return (
                                        <tr key={p.id}>
                                            <td>
                                                <div className="font-semibold text-gray-900 truncate max-w-[120px] md:max-w-none">{p.brand}</div>
                                                <div className="text-sm text-gray-500 truncate max-w-[120px] md:max-w-none">{p.name}</div>
                                            </td>
                                            <td>{p.category}</td>
                                            <td className="text-gray-500 hidden-mobile">{p.unitType}</td>
                                            <td className="text-gray-500 hidden-mobile">€{p.cost.toFixed(2)}</td>
                                            <td>
                                                {isLow ? (
                                                    <span className="badge badge-red">{t('DASHBOARD.LOW_STOCK')}</span>
                                                ) : (
                                                    <span className="badge badge-green">{t('INVENTORY.AUDIT.MATCH')}</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className={`text-xl font-bold ${isLow ? 'text-red-600' : 'text-gray-900'}`}>{p.stock}</div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : view === 'audit' ? (
                <div className="content-card">
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-3">
                            <div className="bg-purple-100 p-2 rounded-lg text-purple-700">
                                <ClipboardCheck size={24} />
                            </div>
                            <div>
                                <h3 className="font-bold text-lg text-gray-900">{t('INVENTORY.AUDIT.MODE_TITLE')}</h3>
                                <p className="text-sm text-gray-500">{t('INVENTORY.AUDIT.MODE_DESC')}</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setView('stock')}
                                className="btn btn-secondary"
                            >
                                {t('COMMON.CANCEL')}
                            </button>
                            <button
                                onClick={handleFinishAudit}
                                disabled={isSubmittingAudit}
                                className="btn btn-primary"
                            >
                                {isSubmittingAudit ? <Loader2 className="animate-spin" /> : t('INVENTORY.AUDIT.FINALIZE')}
                            </button>
                        </div>
                    </div>

                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>{t('PRODUCTS.TABLE.PRODUCT')}</th>
                                    <th>{t('PRODUCTS.TABLE.CATEGORY')}</th>
                                    <th className="text-center bg-gray-50 border-x border-gray-100">{t('INVENTORY.AUDIT.SYSTEM_STOCK')}</th>
                                    <th className="text-center w-32">{t('INVENTORY.AUDIT.ACTUAL_COUNT')}</th>
                                    <th className="text-center">{t('COMMON.STATUS')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map(p => {
                                    const actual = auditCounts[p.id] ?? '';
                                    const diff = actual !== '' ? parseInt(actual) - p.stock : 0;

                                    return (
                                        <tr key={p.id} className={actual !== '' && diff !== 0 ? 'bg-yellow-50' : ''}>
                                            <td className="font-medium text-gray-900">{p.brand} {p.name}</td>
                                            <td className="text-gray-500">{p.category}</td>
                                            <td className="text-center font-mono text-gray-600 bg-gray-50 border-x border-gray-100">
                                                {p.stock}
                                            </td>
                                            <td className="text-center p-2">
                                                <input
                                                    type="number"
                                                    className="w-20 text-center font-bold border border-gray-300 rounded-lg py-1 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                                                    placeholder="-"
                                                    value={actual}
                                                    onChange={(e) => handleAuditInput(p.id, e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            // Logic to move to next input could go here
                                                            e.currentTarget.closest('tr').nextElementSibling?.querySelector('input')?.focus();
                                                        }
                                                    }}
                                                />
                                            </td>
                                            <td className="text-center">
                                                {actual !== '' ? (
                                                    diff === 0 ? (
                                                        <span className="text-green-600 text-sm font-medium flex items-center justify-center gap-1">
                                                            <Check size={14} /> {t('INVENTORY.AUDIT.MATCH')}
                                                        </span>
                                                    ) : diff > 0 ? (
                                                        <span className="text-blue-600 text-sm font-medium">
                                                            +{diff} ({t('INVENTORY.AUDIT.FOUND')})
                                                        </span>
                                                    ) : (
                                                        <span className="text-red-600 text-sm font-medium">
                                                            {diff} ({t('INVENTORY.AUDIT.LOSS')})
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="text-gray-300 text-sm">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="content-card">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>{t('INVENTORY.TABLE.TIME')} / {t('COMMON.DATE')}</th>
                                    <th>{t('PRODUCTS.TABLE.PRODUCT')}</th>
                                    <th>{t('INVENTORY.TABLE.TYPE')}</th>
                                    <th>{t('INVENTORY.TABLE.DETAILS')}</th>
                                    <th>{t('INVENTORY.TABLE.PERFORMED_BY')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movements?.map(m => (
                                    <tr key={m.id}>
                                        <td>
                                            <div className="font-medium text-gray-900">{format(new Date(m.date), 'MMM d, yyyy')}</div>
                                            <div className="text-xs text-gray-500">{format(new Date(m.date), 'HH:mm')}</div>
                                        </td>
                                        <td className="font-medium">{m.productName}</td>
                                        <td>
                                            <span className={`badge ${m.type === 'in' ? 'badge-green' : 'badge-gray'}`}>
                                                {m.type === 'in' ? '+' : '-'} {m.type.toUpperCase()} ({m.qty})
                                            </span>
                                        </td>
                                        <td className="text-gray-600">
                                            {m.reason === 'audit_loss' ? (
                                                <span className="text-red-600 flex items-center gap-1"><AlertTriangle size={12} /> Audit Loss</span>
                                            ) : m.reason === 'audit_found' ? (
                                                <span className="text-blue-600 flex items-center gap-1"><Check size={12} /> Audit Found</span>
                                            ) : (
                                                m.reason
                                            )}
                                            {m.notes && <div className="text-xs text-gray-400 italic mt-0.5">{m.notes}</div>}
                                        </td>
                                        <td>
                                            <div className="flex items-center text-sm text-gray-500">
                                                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 mr-2">
                                                    {m.user?.charAt(0).toUpperCase()}
                                                </div>
                                                {m.user?.split('@')[0]}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {(!movements || movements.length === 0) && (
                                    <tr><td colSpan={5} className="text-center py-12 text-gray-400">No movements logged yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <InventoryModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                products={products}
                onSave={handleSaveMovement}
                currentUser={currentUser}
            />
        </div>
    );
}
